// ─────────────────────────────────────────────────────────────
// ZIFEK FINANCE — types, constantes & utilitaires partagés
// Module de gestion financière pour commerçants, indépendants et
// petites entreprises marocaines. Toutes les données sont scopées
// par `merchant_id` (isolation multi-tenant stricte).
// ─────────────────────────────────────────────────────────────

export type FinanceAccountType = 'cash' | 'bank' | 'wallet' | 'other';
export type TxDirection = 'in' | 'out';
export type TxSource = 'manual' | 'order' | 'reservation' | 'credit' | 'supplier' | 'invoice';
export type InvoiceStatus = 'draft' | 'sent' | 'partially_paid' | 'paid' | 'overdue' | 'cancelled';
export type SupplierTxType = 'invoice' | 'payment';
export type Period = 'today' | 'week' | 'month' | 'year' | 'custom';

export interface FinanceAccount {
  id: number;
  merchant_id: number;
  name: string;
  account_type: FinanceAccountType;
  opening_balance: number;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinanceTransaction {
  id: number;
  merchant_id: number;
  account_id: number | null;
  direction: TxDirection;
  amount: number;
  currency: string;
  category: string | null;
  description: string | null;
  source: TxSource;
  source_ref: string | null;
  counterparty: string | null;
  occurred_at: string;
  created_by: number | null;
  reverses_transaction_id: number | null;
  created_at: string;
}

export interface FinanceExpense {
  id: number;
  merchant_id: number;
  account_id: number | null;
  category: string;
  amount: number;
  currency: string;
  description: string | null;
  vendor: string | null;
  receipt_url: string | null;
  recurring_id: number | null;
  occurred_at: string;
  status: string;
  created_by: number | null;
  created_at: string;
}

export type InvoiceDocType = 'invoice' | 'quote';

export interface FinanceInvoice {
  id: number;
  merchant_id: number;
  number: string;
  /** Facture ou devis. */
  doc_type?: InvoiceDocType | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  customer_address?: string | null;
  customer_ice?: string | null;
  /** Date de la vente / de la prestation (si différente de l'émission). */
  sale_date?: string | null;
  /** Bon de commande ou devis rattaché. */
  reference?: string | null;
  payment_terms?: string | null;
  payment_method?: string | null;
  legal_mentions?: string | null;
  source_quote_id?: number | null;
  status: InvoiceStatus;
  issue_date: string;
  due_date: string | null;
  currency: string;
  subtotal: number;
  discount: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  amount_paid: number;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  items?: FinanceInvoiceItem[];
}

export interface FinanceInvoiceItem {
  id?: number;
  invoice_id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  /** Origine de la ligne : produit du catalogue, service, ou saisie libre. */
  item_type?: 'product' | 'service' | 'custom' | null;
  /** Remise en montant appliquée à la ligne. */
  discount?: number | null;
  product_id?: number | null;
}

export interface FinanceSupplier {
  id: number;
  merchant_id: number;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanceSupplierTransaction {
  id: number;
  merchant_id: number;
  supplier_id: number;
  type: SupplierTxType;
  amount: number;
  currency: string;
  due_date: string | null;
  description: string | null;
  account_id: number | null;
  status: string;
  created_by: number | null;
  created_at: string;
}

export interface FinanceBudget {
  id: number;
  merchant_id: number;
  category: string;
  period: string;
  amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface FinanceRecurringExpense {
  id: number;
  merchant_id: number;
  category: string;
  amount: number;
  currency: string;
  frequency: string;
  next_due_date: string | null;
  account_id: number | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export interface FinanceSettings {
  id?: number;
  merchant_id: number;
  currency: string;
  invoice_prefix: string;
  default_tax_rate: number;
  alerts: FinanceAlertPrefs | null;
}

export interface FinanceAlertPrefs {
  supplier_due: boolean;
  credit_overdue: boolean;
  credit_limit_exceeded: boolean;
  unusual_expense: boolean;
  low_cash: boolean;
  invoice_overdue: boolean;
  recurring_due: boolean;
  revenue_change: boolean;
}

export const DEFAULT_ALERT_PREFS: FinanceAlertPrefs = {
  supplier_due: true,
  credit_overdue: true,
  credit_limit_exceeded: true,
  unusual_expense: true,
  low_cash: true,
  invoice_overdue: true,
  recurring_due: true,
  revenue_change: true,
};

export type FinanceAlertKey = keyof FinanceAlertPrefs;

export const FINANCE_ALERT_META: { key: FinanceAlertKey; label: string; icon: string }[] = [
  { key: 'invoice_overdue', label: 'Facture en retard', icon: 'ri-file-warning-line' },
  { key: 'supplier_due', label: 'Échéance fournisseur', icon: 'ri-truck-line' },
  { key: 'credit_overdue', label: 'Crédit client en retard', icon: 'ri-user-received-line' },
  { key: 'credit_limit_exceeded', label: 'Plafond de crédit dépassé', icon: 'ri-shield-flash-line' },
  { key: 'low_cash', label: 'Trésorerie basse', icon: 'ri-wallet-3-line' },
  { key: 'recurring_due', label: 'Dépense récurrente à échoir', icon: 'ri-loop-left-line' },
  { key: 'unusual_expense', label: 'Dépense inhabituelle', icon: 'ri-alarm-warning-line' },
  { key: 'revenue_change', label: 'Variation du chiffre d’affaires', icon: 'ri-line-chart-line' },
];

export interface FinanceAlert {
  key: FinanceAlertKey;
  severity: 'danger' | 'warning' | 'info';
  message: string;
  amount?: number;
  date?: string | null;
}

export interface AlertsInput {
  invoices: FinanceInvoice[];
  supplierBalances: { name: string; balance: number; dueDate: string | null }[];
  recurring: FinanceRecurringExpense[];
  accountBalances: number[];
  creditOverdue: { name: string; balance: number; limit?: number | null }[];
  expenses: FinanceExpense[];
  currency: string;
}

/**
 * Calcule les alertes à partir des données réelles du marchand.
 * Rien n'est inventé : une alerte n'apparaît que si la donnée existe.
 */
export function computeFinanceAlerts(input: AlertsInput, prefs: FinanceAlertPrefs): FinanceAlert[] {
  const alerts: FinanceAlert[] = [];
  const now = Date.now();
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const in7days = now + 7 * 24 * 60 * 60 * 1000;

  // Factures en retard
  if (prefs.invoice_overdue) {
    input.invoices
      .filter((i) => i.due_date && i.status !== 'paid' && i.status !== 'cancelled' && new Date(i.due_date).getTime() < startOfDay.getTime())
      .forEach((i) => {
        alerts.push({
          key: 'invoice_overdue',
          severity: 'danger',
          message: `Facture ${i.number}${i.customer_name ? ` — ${i.customer_name}` : ''} en retard`,
          amount: Math.max(0, i.total - i.amount_paid),
          date: i.due_date,
        });
      });
  }

  // Échéances fournisseurs (dette > 0)
  if (prefs.supplier_due) {
    input.supplierBalances
      .filter((s) => s.balance > 0)
      .forEach((s) => {
        const late = s.dueDate ? new Date(s.dueDate).getTime() < startOfDay.getTime() : false;
        alerts.push({
          key: 'supplier_due',
          severity: late ? 'danger' : 'warning',
          message: `Fournisseur ${s.name} à payer`,
          amount: s.balance,
          date: s.dueDate,
        });
      });
  }

  // Crédit client en retard
  if (prefs.credit_overdue) {
    input.creditOverdue
      .filter((c) => c.balance > 0)
      .forEach((c) => {
        alerts.push({
          key: 'credit_overdue',
          severity: 'warning',
          message: `${c.name} vous doit encore`,
          amount: c.balance,
        });
      });
  }

  // Plafond de crédit dépassé : le solde dû atteint ou dépasse le plafond défini
  if (prefs.credit_limit_exceeded) {
    input.creditOverdue
      .filter((c) => typeof c.limit === 'number' && c.limit > 0 && c.balance >= c.limit)
      .forEach((c) => {
        const limit = Number(c.limit) || 0;
        const over = c.balance - limit;
        alerts.push({
          key: 'credit_limit_exceeded',
          severity: 'danger',
          message:
            over > 0
              ? `${c.name} a dépassé son plafond de crédit (+${formatMoney(over, input.currency)})`
              : `${c.name} a atteint son plafond de crédit`,
          amount: c.balance,
        });
      });
  }

  // Trésorerie basse (< 500 en devise locale ou négative)
  if (prefs.low_cash) {
    const total = input.accountBalances.reduce((s, v) => s + v, 0);
    if (input.accountBalances.length > 0 && total < 500) {
      alerts.push({
        key: 'low_cash',
        severity: total < 0 ? 'danger' : 'warning',
        message: total < 0 ? 'Trésorerie négative' : 'Trésorerie disponible faible',
        amount: total,
      });
    }
  }

  // Dépenses récurrentes à échoir (7 jours)
  if (prefs.recurring_due) {
    input.recurring
      .filter((r) => r.is_active && r.next_due_date)
      .filter((r) => {
        const t = new Date(r.next_due_date as string).getTime();
        return t >= startOfDay.getTime() && t <= in7days;
      })
      .forEach((r) => {
        alerts.push({
          key: 'recurring_due',
          severity: 'info',
          message: `Dépense récurrente à échoir : ${r.description || r.category}`,
          amount: Number(r.amount),
          date: r.next_due_date,
        });
      });
  }

  // Dépense inhabituelle : une dépense > 3× la moyenne du mois
  if (prefs.unusual_expense && input.expenses.length >= 3) {
    const total = input.expenses.reduce((s, e) => s + Number(e.amount), 0);
    const avg = total / input.expenses.length;
    const biggest = input.expenses.reduce((m, e) => (Number(e.amount) > Number(m.amount) ? e : m), input.expenses[0]);
    if (Number(biggest.amount) > avg * 3) {
      alerts.push({
        key: 'unusual_expense',
        severity: 'info',
        message: `Dépense inhabituelle : ${biggest.description || expenseCategoryMeta(biggest.category).label}`,
        amount: Number(biggest.amount),
        date: biggest.occurred_at,
      });
    }
  }

  return alerts;
}

export const FINANCE_CURRENCIES = ['MAD', 'EUR', 'USD', 'GBP', 'XOF', 'XAF', 'DZD', 'TND'];

export const EXPENSE_CATEGORIES: { key: string; label: string; icon: string }[] = [
  { key: 'marchandise', label: 'Marchandise', icon: 'ri-shopping-bag-3-line' },
  { key: 'loyer', label: 'Loyer', icon: 'ri-home-4-line' },
  { key: 'electricite', label: 'Électricité', icon: 'ri-flashlight-line' },
  { key: 'eau', label: 'Eau', icon: 'ri-water-flash-line' },
  { key: 'internet', label: 'Internet', icon: 'ri-wifi-line' },
  { key: 'transport', label: 'Transport', icon: 'ri-truck-line' },
  { key: 'livraison', label: 'Livraison', icon: 'ri-e-bike-2-line' },
  { key: 'salaires', label: 'Salaires', icon: 'ri-user-star-line' },
  { key: 'marketing', label: 'Marketing', icon: 'ri-megaphone-line' },
  { key: 'equipement', label: 'Équipement', icon: 'ri-tools-line' },
  { key: 'logiciel', label: 'Logiciel', icon: 'ri-apps-2-line' },
  { key: 'commissions', label: 'Commissions', icon: 'ri-percent-line' },
  { key: 'frais_bancaires', label: 'Frais bancaires', icon: 'ri-bank-line' },
  { key: 'taxes', label: 'Taxes', icon: 'ri-file-list-3-line' },
  { key: 'autre', label: 'Autre', icon: 'ri-more-line' },
];

export function expenseCategoryMeta(key: string | null | undefined) {
  return EXPENSE_CATEGORIES.find((c) => c.key === key) || { key: 'autre', label: 'Autre', icon: 'ri-more-line' };
}

export const ACCOUNT_TYPES: { key: FinanceAccountType; label: string; icon: string }[] = [
  { key: 'cash', label: 'Caisse', icon: 'ri-money-dollar-box-line' },
  { key: 'bank', label: 'Compte bancaire', icon: 'ri-bank-line' },
  { key: 'wallet', label: 'Portefeuille mobile', icon: 'ri-smartphone-line' },
  { key: 'other', label: 'Autre', icon: 'ri-wallet-3-line' },
];

export function accountTypeMeta(key: FinanceAccountType) {
  return ACCOUNT_TYPES.find((a) => a.key === key) || ACCOUNT_TYPES[3];
}

export const REVENUE_CATEGORIES = ['Vente', 'Service', 'Commande', 'Autre'];

export const INVOICE_STATUS_META: Record<InvoiceStatus, { label: string; className: string }> = {
  draft: { label: 'Brouillon', className: 'bg-background-200/70 text-foreground-600' },
  sent: { label: 'Envoyée', className: 'bg-secondary-100 text-secondary-700' },
  partially_paid: { label: 'Partiellement payée', className: 'bg-accent-100 text-accent-700' },
  paid: { label: 'Payée', className: 'bg-primary-100 text-primary-700' },
  overdue: { label: 'En retard', className: 'bg-red-100 text-red-600' },
  cancelled: { label: 'Annulée', className: 'bg-background-200/70 text-foreground-500' },
};

export const INVOICE_STATUSES: InvoiceStatus[] = ['draft', 'sent', 'partially_paid', 'paid', 'overdue', 'cancelled'];

// ── Formatage ─────────────────────────────────────────────────

export function formatMoney(amount: number, currency = 'MAD'): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const formatted = value.toLocaleString('fr-FR', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} ${currency}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Périodes & séries ─────────────────────────────────────────

export function periodRange(period: Period, customStart?: string, customEnd?: string): { start: Date; end: Date } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  if (period === 'today') {
    return { start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0), end };
  }
  if (period === 'week') {
    const day = now.getDay() === 0 ? 7 : now.getDay();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (day - 1), 0, 0, 0, 0);
    return { start, end };
  }
  if (period === 'year') {
    return { start: new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0), end };
  }
  if (period === 'custom' && customStart) {
    const start = new Date(customStart);
    start.setHours(0, 0, 0, 0);
    const customEndDate = customEnd ? new Date(customEnd) : end;
    customEndDate.setHours(23, 59, 59, 999);
    return { start, end: customEndDate };
  }
  // month (défaut)
  return { start: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0), end };
}

export function inRange(iso: string, range: { start: Date; end: Date }): boolean {
  const t = new Date(iso).getTime();
  return t >= range.start.getTime() && t <= range.end.getTime();
}

// ── KPI ───────────────────────────────────────────────────────

export interface FinanceKpis {
  revenue: number;
  expenses: number;
  profit: number;
  cash: number;
  receivables: number;
  payables: number;
  pendingPayments: number;
  cashForecast: number;
}

export function sumTransactions(txs: FinanceTransaction[], direction: TxDirection): number {
  return txs.filter((t) => t.direction === direction).reduce((s, t) => s + (Number(t.amount) || 0), 0);
}

export function sumExpenses(exps: FinanceExpense[]): number {
  return exps.reduce((s, e) => s + (Number(e.amount) || 0), 0);
}

/** Solde d'un compte = ouverture + entrées − sorties. */
export function accountBalance(
  account: FinanceAccount,
  transactions: FinanceTransaction[],
  expenses: FinanceExpense[]
): number {
  const txs = transactions.filter((t) => t.account_id === account.id);
  const exps = expenses.filter((e) => e.account_id === account.id);
  return (Number(account.opening_balance) || 0) + sumTransactions(txs, 'in') - sumTransactions(txs, 'out') - sumExpenses(exps);
}

/** Série mensuelle revenus / dépenses / bénéfice sur N mois. */
export function monthlySeries(
  transactions: FinanceTransaction[],
  expenses: FinanceExpense[],
  months = 6
): { key: string; label: string; revenue: number; expense: number; profit: number }[] {
  const now = new Date();
  const out: { key: string; label: string; revenue: number; expense: number; profit: number }[] = [];
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const revenue = transactions
      .filter((t) => t.direction === 'in')
      .filter((t) => {
        const time = new Date(t.occurred_at).getTime();
        return time >= start && time <= end;
      })
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const expense = expenses
      .filter((e) => {
        const time = new Date(e.occurred_at).getTime();
        return time >= start && time <= end;
      })
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
    out.push({
      key,
      label: d.toLocaleDateString('fr-FR', { month: 'short' }),
      revenue,
      expense,
      profit: revenue - expense,
    });
  }
  return out;
}